// Estado da pausa do próprio atendente, compartilhado entre o seletor de status
// (sidebar) e o BreakBanner global. Persiste em localStorage pra sobreviver a
// navegação/reload (o GET /chat/me/availability não devolve o activeBreak).

// #538: a chave é POR USUÁRIO. A pausa sobrevive ao logout por desenho
// (gk-chat#280), e num navegador compartilhado a chave única `gk:break` fazia a
// pausa de um atendente aparecer para quem entrasse depois — sem poder
// encerrá-la, porque ela não é dele.
const LEGACY_KEY = "gk:break";
export const BREAK_EVENT = "gk:break:changed";

/**
 * Chave do usuário logado; `null` sem usuário (sem dono, não há pausa).
 *
 * Lida a CADA chamada, de propósito: o `user` e o token vivem no mesmo
 * localStorage compartilhado, e um login em outra aba troca os dois juntos
 * (o `api` propaga o token novo até aos sockets vivos desta aba). Fixar o dono
 * por aba deixaria a chave de um usuário e o token de outro — e a troca de
 * usuário sem `clearTokens` nesta aba (redirect do `useAuth` pro /login)
 * reabriria o vazamento da #538.
 */
export function keyDoUsuario(): string | null {
  try {
    // A chave antiga não tem dono: herdá-la é exatamente o vazamento.
    localStorage.removeItem(LEGACY_KEY);
    const raw = localStorage.getItem("user");
    const id = raw ? (JSON.parse(raw) as { id?: unknown }).id : null;
    return typeof id === "string" && id ? `${LEGACY_KEY}:${id}` : null;
  } catch {
    return null;
  }
}

export interface ActiveBreakState {
  /** Nome do motivo da pausa (ex: "Treinamento"). */
  reason: string | null;
  /**
   * ISO do início — base do contador. `null` quando a pausa foi declarada pelo
   * motor (issue #450) e não temos o início: o snapshot devolve
   * `activeBreak: null` e o `presence.update` só carrega availability, então
   * uma pausa iniciada em outra sessão/dispositivo chega sem `started_at`.
   * Nesse caso o indicador aparece sem duração — nunca contando do zero.
   */
  startedAt: string | null;
  /** UI: faixa minimizada (true) vs overlay central (false). */
  minimized: boolean;
  /**
   * Limite do motivo em segundos (Platform, motivos de pausa com limite).
   * `null` = motivo sem limite; ausente = ninguém resolveu (pausa vinda de outra
   * sessão/evento sem o dado) — o banner trata os dois como pausa livre.
   */
  maxSeconds?: number | null;
}

export function getBreakState(): ActiveBreakState | null {
  if (typeof window === "undefined") return null;
  const key = keyDoUsuario();
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as ActiveBreakState) : null;
  } catch {
    return null;
  }
}

export function setBreakState(s: ActiveBreakState): void {
  if (typeof window === "undefined") return;
  const key = keyDoUsuario();
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(s));
  } catch {
    // localStorage indisponível — segue sem persistir
  }
  window.dispatchEvent(new Event(BREAK_EVENT));
}

export function clearBreakState(): void {
  if (typeof window === "undefined") return;
  const key = keyDoUsuario();
  try {
    if (key) localStorage.removeItem(key);
  } catch {
    // noop
  }
  window.dispatchEvent(new Event(BREAK_EVENT));
}
